import configuration from "./index";
import { authFetch } from "../utils/authFetch";
import { logWarning } from "../utils/errorLogging";

/**
 * Server-side max page size for the /timeline endpoint. The backend clamps any
 * larger limit down to this value, so requesting it minimizes the number of
 * pages (the backend re-merges the full timeline on every request).
 */
export const TIMELINE_PAGE_LIMIT = 200;

/**
 * Upper bound on the number of pages getAllConsolidatedTimeline will fetch
 * (≈2000 events). A hard cap guards against an unexpected server response that
 * never returns a short page, which would otherwise loop indefinitely.
 */
export const MAX_TIMELINE_PAGES = 10;

/**
 * Timeline API Connection
 * Provides access to the consolidated timeline endpoint that merges notes,
 * communications, and workflow invocations into a single chronological view.
 */
export const TimelineAPI = {
  /**
   * Get consolidated timeline events for a problem/outage
   * @param {string} problem - Problem ID
   * @param {string} outage - Outage ID
   * @param {number} limit - Maximum number of events to return (default: 50, max: 200)
   * @param {number} offset - Number of events to skip for pagination (default: 0)
   * @returns {Promise<Object>} Timeline response with events array and metadata
   */
  getConsolidatedTimeline: async function (
    problem,
    outage,
    limit = 50,
    offset = 0,
  ) {
    // Validate parameters
    if (!problem || !outage) {
      throw new Error("Problem ID and Outage ID are required");
    }

    // Ensure limit is within bounds
    if (limit < 1 || limit > TIMELINE_PAGE_LIMIT) {
      console.warn(`Invalid limit ${limit}, using default of 50`);
      limit = 50;
    }

    // Ensure offset is non-negative
    if (offset < 0) {
      console.warn(`Invalid offset ${offset}, using 0`);
      offset = 0;
    }

    const requestOptions = {
      method: "GET",
      headers: { "Content-type": "application/json" },
    };

    // Build API URL with query parameters
    const apiURL = `${configuration.apiBase}/problem/${problem}/outage/${encodeURIComponent(outage)}/timeline?limit=${limit}&offset=${offset}`;

    try {
      const response = await authFetch(apiURL, requestOptions);

      // Handle HTTP errors
      if (!response.ok) {
        console.error("Timeline API request failed:", {
          status: response.status,
          statusText: response.statusText,
          url: apiURL,
        });

        // Handle other errors
        throw new Error(
          `Failed to fetch timeline: ${response.status} ${response.statusText}`,
        );
      }

      // Parse and return JSON response
      const data = await response.json();

      // Validate response structure
      if (!data || !Array.isArray(data.events)) {
        console.error("Invalid timeline response structure:", data);
        throw new Error("Invalid timeline response from server");
      }

      return data;
    } catch (error) {
      // Log error for debugging
      console.error("Error fetching consolidated timeline:", error);
      throw error;
    }
  },

  /**
   * Get the COMPLETE consolidated timeline by paging the /timeline endpoint.
   *
   * The endpoint returns events newest-first and clamps limit to 200. This
   * helper loops over pages (offset += TIMELINE_PAGE_LIMIT), accumulating
   * events until a short page is returned (or the server total is reached),
   * so callers get the full history in a single call. A hard MAX_TIMELINE_PAGES
   * cap prevents an unbounded loop; when hit, it logs a warning and returns what
   * was fetched rather than failing (silent truncation is the bug this fixes).
   *
   * Mirrors the offset-accumulate pattern in
   * components/Viewer/ChronoViewer.js (loadMoreEvents).
   *
   * @param {string} problem - Problem ID
   * @param {string} outage - Outage ID
   * @param {AbortSignal} [signal] - Optional abort signal; checked before each page
   * @returns {Promise<Array>} The full accumulated events array. When the timeline
   *   is cut short (the MAX_TIMELINE_PAGES safety cap is hit or a later page fails
   *   mid-loop), the condition is surfaced via a logWarning (which reaches New Relic
   *   as an AppWarning event) and the pages fetched so far are returned.
   */
  getAllConsolidatedTimeline: async function (problem, outage, signal = null) {
    const accumulated = [];
    let offset = 0;
    let total = 0;
    let page = 0;

    while (page < MAX_TIMELINE_PAGES) {
      // Bail early if the caller aborted (e.g. incident switched).
      if (signal?.aborted) {
        const abortError = new Error("Timeline fetch aborted");
        abortError.name = "AbortError";
        throw abortError;
      }

      let response;
      try {
        response = await this.getConsolidatedTimeline(
          problem,
          outage,
          TIMELINE_PAGE_LIMIT,
          offset,
        );
      } catch (pageError) {
        // First page failing = nothing to salvage; let the caller fall back to
        // notes. A later page failing = keep the pages already fetched and
        // surface it loudly rather than blanking the whole timeline.
        if (page === 0) {
          throw pageError;
        }
        logWarning(
          "TimelineAPI.getAllConsolidatedTimeline",
          "Timeline paging failed mid-loop; returning the pages fetched so far",
          {
            problem,
            outage,
            pagesFetched: page,
            eventsFetched: accumulated.length,
            error: pageError.message,
          },
        );
        return accumulated;
      }

      const pageEvents = response.events || [];
      accumulated.push(...pageEvents);
      // total is used only by the terminator below; the server value can drift
      // on a live incident, so the short page is the primary end signal.
      total = response.total || accumulated.length;
      page += 1;

      // Reached the true end: a short page, or we've collected the server total.
      // (accumulated spans offset 0..current, so compare it directly to total —
      // adding offset would double-count events already accumulated.)
      if (pageEvents.length < TIMELINE_PAGE_LIMIT || accumulated.length >= total) {
        return accumulated;
      }

      offset += TIMELINE_PAGE_LIMIT;
    }

    // Fell out of the loop with pages still remaining: the MAX_TIMELINE_PAGES
    // safety cap was hit. Surface it loudly (reaches New Relic as an AppWarning)
    // and return what we fetched rather than looping unbounded.
    logWarning(
      "TimelineAPI.getAllConsolidatedTimeline",
      "Timeline paging hit MAX_TIMELINE_PAGES cap; returning a truncated timeline",
      {
        problem,
        outage,
        pagesFetched: page,
        eventsFetched: accumulated.length,
        serverTotal: total,
      },
    );
    return accumulated;
  },
};
